// ============================================================================
// GUARDS/ANALYTICS-GUARD — Auth + profile + view-level permission gate (Stage 9)
//
// Wraps any analytics view. Enforces 5 invariants via fresh Firestore read:
//   INV 1: /users/{uid} is readable and doc exists
//   INV 2: profile.isActive === true
//   INV 3: profile.role ≠ 'pending'
//   INV 4: profile.tenantId is a non-empty string
//   INV 5: canAccessAnalyticsView(role, requiredView) === true
//
// Shows appropriate guard UI for each invariant violation.
// Renders children only when all invariants pass.
// No silent blanks: every state has explicit UI.
// ============================================================================

import React, { useContext } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import { useUserProfile } from '../hooks/useUserProfile.js';
import { canAccessAnalyticsView } from '../rbac.js';
import { safeUserError } from '../safeError.js';

// ── Internal guard-state components ──────────────────────────────────────────

function GuardSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
    </div>
  );
}

function AnalyticsErrorView({ message }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center px-6">
      <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center">
        <AlertTriangle className="w-7 h-7 text-amber-400" />
      </div>
      <p className="text-sm font-medium text-slate-200">Unable to load analytics</p>
      <p className="text-xs text-slate-400 max-w-xs">{message}</p>
    </div>
  );
}

function AnalyticsAccountBlocked({ message }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center px-6">
      <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center">
        <AlertTriangle className="w-7 h-7 text-amber-400" />
      </div>
      <p className="text-sm font-medium text-slate-200">Account access restricted</p>
      <p className="text-xs text-slate-400 max-w-xs">{message}</p>
    </div>
  );
}

function AnalyticsAccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center px-6">
      <div className="w-14 h-14 rounded-2xl bg-slate-800/50 flex items-center justify-center">
        <Lock className="w-7 h-7 text-slate-500" />
      </div>
      <p className="text-sm font-medium text-slate-200">Access denied</p>
      <p className="text-xs text-slate-400 max-w-xs">
        Your role does not have permission to view this analytics page.
      </p>
    </div>
  );
}

// ── AnalyticsGuard ────────────────────────────────────────────────────────────

/**
 * Validates auth + profile + view-level permissions before rendering children.
 * Performs a fresh /users/{uid} Firestore read on mount — does not rely on
 * cached AuthContext profile.
 *
 * @param {React.ReactNode} children                        — analytics view to guard
 * @param {'OPS'|'PERFORMANCE'|'DEEP_DIVE'} requiredView   — view key for RBAC check
 */
export default function AnalyticsGuard({ children, requiredView }) {
  const { user } = useContext(AuthContext);
  const uid = user?.uid ?? null;

  const { loading, error, profile } = useUserProfile(uid);

  // ── Guard loading state (no silent blank) ────────────────────────────────
  if (loading) return <GuardSpinner />;

  // ── INV 1: profile must be readable and doc must exist ───────────────────
  if (error || !profile) {
    return (
      <AnalyticsErrorView
        message={
          error
            ? safeUserError(error)
            : 'Your profile could not be loaded. Contact your administrator.'
        }
      />
    );
  }

  // ── INV 2: account must be active ────────────────────────────────────────
  if (!profile.isActive) {
    return (
      <AnalyticsAccountBlocked
        message="Your account has been deactivated. Contact your administrator."
      />
    );
  }

  // ── INV 3: role must not be pending ──────────────────────────────────────
  if (profile.role === 'pending') {
    return (
      <AnalyticsAccountBlocked
        message="Your account is pending approval. Contact your administrator."
      />
    );
  }

  // ── INV 4: tenantId must be a non-empty string ───────────────────────────
  if (!profile.tenantId || typeof profile.tenantId !== 'string') {
    return (
      <AnalyticsErrorView
        message="Your account is not linked to an organisation. Contact your administrator."
      />
    );
  }

  // ── INV 5: view-level role check ─────────────────────────────────────────
  if (!canAccessAnalyticsView(profile.role, requiredView)) {
    return <AnalyticsAccessDenied />;
  }

  // ── All invariants pass — render analytics view ──────────────────────────
  return <>{children}</>;
}
